import { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'redux-bundler-react';

import StepHeader from '@components/step-header/StepHeader';
import statusModal from '@forms/components/modals/statusModal';
import TextInput from '@components/text-input/TextInput';
import SelectInput from '@components/select/Select';
import TextAreaInput from '@components/textarea/TextArea';
import CreatableSelectInput from '@components/new-inputs/creatableSelectInput';
import ErrorSummary from '@components/error-summary/ErrorSummary';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';

import { siteDirectionsTooltipContent } from '@forms/input-forms/helpers';
import {
  projectDescriptionTooltipContent,
  projectPurposeTooltipContent,
  natureOfActivityTooltipContent,
} from '@pages/Resources/components/_shared/helper';
import { isValidORMNumber } from '@src/utils/helpers';

import { useRequest, useModal } from '@stores';

export const GeneralProjectInformationMetadata = {
  sectionName: 'General Project Information',
};

const schema = yup.object().shape({
  projectName: yup.string().required('Field is required'),
  hasPreviousFileNumber: yup.string().required('Please select an option'),
  previousFileNumbers: yup.array().when('hasPreviousFileNumber', {
    is: 'true',
    then: () =>
      yup
        .array()
        .min(1, 'At least one Previous File Number is required')
        .required('At least one Previous File Number is required'),
  }),
  projectDescription: yup
    .string()
    .test(
      'isDefault',
      'DEFAULT_VALUE is a reserved word, please enter a valid string',
      (val) => val.toUpperCase() !== 'DEFAULT_VALUE'
    )
    .required('Field is required'),
  projectPurpose: yup
    .string()
    .test(
      'isDefault',
      'DEFAULT_VALUE is a reserved word, please enter a valid string',
      (val) => val.toUpperCase() !== 'DEFAULT_VALUE'
    )
    .required('Field is required'),
  natureOfActivity: yup
    .string()
    .test(
      'isDefault',
      'DEFAULT_VALUE is a reserved word, please enter a valid string',
      (val) => val.toUpperCase() !== 'DEFAULT_VALUE'
    )
    .required('Field is required'),
  siteDirections: yup.string().nullable(),
});

const GeneralProjectInformation = connect(
  'doUpdateRequestData',
  'selectRequestFormData',
  'selectRequestAPIData',
  ({ doUpdateRequestData, requestFormData, requestAPIData, stepNo }) => {
    const { openSecondaryModal } = useModal();
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();

    const convertFileNumbersToSelectFormat = useCallback((data) => {
      if (!Array.isArray(data)) return [];

      return data.map((item) => {
        const value = typeof item === 'string' ? item : (item?.ormFileNumber ?? item?.value ?? '');

        const upper = value.toUpperCase();
        return { label: upper, value: upper };
      });
    }, []);

    const sourceData = requestFormData ?? requestAPIData;
    const savedHasPreviousFileNumber =
      sourceData?.previousFileNumbers?.length > 0 ||
      sourceData?.hasPreviousFileNumber === true ||
      sourceData?.hasPreviousFileNumber === 'true'
        ? 'true'
        : 'false';

    const defaultValues = useMemo(
      () => ({
        projectName: sourceData?.projectName ?? '',
        hasPreviousFileNumber: savedHasPreviousFileNumber,
        previousFileNumbers:
          savedHasPreviousFileNumber === 'true' && sourceData?.previousFileNumbers?.length > 0
            ? convertFileNumbersToSelectFormat(sourceData.previousFileNumbers)
            : [],
        projectDescription: sourceData?.projectDescription ?? '',
        projectPurpose: sourceData?.projectPurpose ?? '',
        natureOfActivity: sourceData?.natureOfActivity ?? '',
        siteDirections: sourceData?.siteDirections ?? '',
      }),
      [sourceData, savedHasPreviousFileNumber, convertFileNumbersToSelectFormat]
    );

    const methods = useForm({ resolver: yupResolver(schema), mode: 'onBlur', defaultValues: defaultValues });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      getValues,
      trigger,
      reset,
    } = methods;
    const isDirty = Object.keys(dirtyFields).length > 0;
    const isValid = Object.keys(errors).length === 0;
    const isTouched = isSectionTouched(stepNo);

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSection]);

    const hasPreviousFileNumber = watch('hasPreviousFileNumber');
    const previousFileNumbers = watch('previousFileNumbers');

    //Reset the dirty states of the fields after a save. Only trigger validation for steps that have been touched.
    useEffect(() => {
      reset(defaultValues, {
        keepDirty: true,
        keepErrors: true,
      });

      if (isTouched) {
        trigger();
      }
    }, [defaultValues, isTouched, reset, trigger]);

    const handleChange = () => {
      if (!isReadOnly) {
        const values = getValues();
        const prevFileNumbers =
          values?.hasPreviousFileNumber === 'false'
            ? []
            : values?.previousFileNumbers?.length > 0
              ? values?.previousFileNumbers?.map((val) => ({ ormFileNumber: val?.value?.toUpperCase() }))
              : [];
        doUpdateRequestData({
          hasPreviousFileNumber: values?.hasPreviousFileNumber,
          natureOfActivity: values?.natureOfActivity,
          previousFileNumbers: prevFileNumbers,
          projectDescription: values?.projectDescription,
          projectName: values?.projectName,
          projectPurpose: values?.projectPurpose,
          siteDirections: values?.siteDirections,
          location: { ...sourceData.location },
        });
      }
    };

    const handleSelectChange = (e) => {
      const results = e.map((num) => isValidORMNumber(num.value));

      if (!results.every(Boolean)) {
        setValue('previousFileNumbers', e.length > 1 ? previousFileNumbers : []);
        openSecondaryModal(statusModal, {
          status: 'Failed',
          msg: (
            <>
              <p>An Invalid ORM file number was detected. ORM file numbers should conform to the following:</p>
              <ol>
                <li>
                  First 3 characters should be one of the following: MVM, MVN, MVS, MVK, MVR, MVP, NAB, NAN, NAO, NAP,
                  NAE, NWP, NWW, NWK, NWO, NWS, LRH, LRL, LRN, LRP, LRB, LRC, LRE, POH, POA, SAJ, SAC, SAM, SAS, SAW,
                  SPL, SPK, SPN, SPA, SWF, SWG, SWL, SWT, SAA
                </li>
                <br />
                <li>4th character is a dash "-"</li>
                <br />
                <li>Characters 5-8 should be a 4 digit number between 1900-the current year</li>
                <br />
                <li>9th character is a dash "-"</li>
                <br />
                <li>Remaining characters could be 5 or 6 digits and may only be numbers.</li>
                <li>An optional 5 character suffix is also supported.</li>
              </ol>
              <p>Example: MVM-2023-12345 or MVS-2020-123456-MT123 </p>
            </>
          ),
        });
      }
    };

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader text='General Project Information' />

        <TextInput
          label='Project Name'
          name='projectName'
          type='text'
          required
          tooltip='Please provide name identifying the proposed project, e.g., Landmark Plaza, Burned Hills Subdivision, or Edsall Commercial Center'
          readOnly={isReadOnly}
          onChange={handleChange}
        />
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='hasPreviousFileNumber'
            label='Has the USACE previously issued a file number for any part of the project area?'
            required
            readOnly={isReadOnly}
            onChange={handleChange}
          >
            <option key='2' value='true'>
              Yes
            </option>
            <option key='3' value='false'>
              No
            </option>
          </SelectInput>
        </div>
        {hasPreviousFileNumber === 'true' && (
          <CreatableSelectInput
            name='previousFileNumbers'
            label='Previous File Number(s)'
            tooltip='Enter the file number and press "ENTER" or click on "Create ..."'
            placeholder='e.g. MVS-2023-12345, MVM-2020-1234 ...'
            required
            multi
            handleChange={handleSelectChange}
            handleBlur={handleChange}
            readOnly={isReadOnly}
          />
        )}

        <TextAreaInput
          name='projectDescription'
          label='Project Description'
          tooltip={projectDescriptionTooltipContent}
          readOnly={isReadOnly}
          onChange={handleChange}
          required
        />

        <TextAreaInput
          name='projectPurpose'
          label='Project Purpose'
          tooltip={projectPurposeTooltipContent}
          readOnly={isReadOnly}
          onChange={handleChange}
          required
        />

        <TextAreaInput
          name='natureOfActivity'
          label='Nature of Activity'
          tooltip={natureOfActivityTooltipContent}
          readOnly={isReadOnly}
          onChange={handleChange}
          required
        />

        <TextAreaInput
          name='siteDirections'
          label='Directions to Project Location'
          tooltip={siteDirectionsTooltipContent}
          readOnly={isReadOnly}
          onChange={handleChange}
        />
      </FormProvider>
    );
  }
);

GeneralProjectInformation.metadata = GeneralProjectInformationMetadata;

export default GeneralProjectInformation;
